import React, { useState, useMemo } from 'react';
import * as Recharts from 'recharts';
import { useSelector } from 'react-redux';
import { isEmpty, zipWith } from 'lodash';
import { Dropdown } from 'react-bootstrap';

import ChartCard from '../../ChartComponents/ChartCard';
import ChartTooltip from '../../ChartComponents/ChartTooltip';
import Info from '../../../../components/Info';
import Authorize from '../../../../components/Authorize';
import { CONFIG } from '../../../../components/Authorize/permissions/resources';

import { formatDateToTimezone } from '../../../../helpers/utils';

const initialChartLine = {
  angry: true,
  calm: true,
  confused: true,
  disgusted: true,
  fear: true,
  happy: true,
  sad: true,
  surprised: true,
};

const EmotionTimeline = () => {
  const timezone = useSelector(({ events: { currentEvent } }) => currentEvent?.timezone);
  const {
    emotions: emotions15m,
    emotions1m,
    emotions5m,
    emotions30m,
  } = useSelector(({ analytics: { briziVision: { data } } }) => data);

  const [chartLines, setChartLines] = useState(initialChartLine);
  const [selectedInterval, setSelectedInterval] = useState('15m');

  // Get the current emotions data based on selected interval
  const currentEmotions = useMemo(() => {
    switch (selectedInterval) {
    case '1m':
      return emotions1m;
    case '5m':
      return emotions5m;
    case '30m':
      return emotions30m;
    case '15m':
    default:
      return emotions15m;
    }
  }, [selectedInterval, emotions15m, emotions1m, emotions5m, emotions30m]);

  const {
    angry = [],
    calm = [],
    confused = [],
    disgusted = [],
    fear = [],
    happy = [],
    sad = [],
    surprised = [],
  } = currentEmotions || {};

  const handleLegendClick = (series) => {
    setChartLines({ ...chartLines, [series]: !chartLines[series] });
  };

  const chartData = zipWith(angry, calm, confused, disgusted, fear, happy, sad, surprised, (
    angryEntry,
    calmEntry,
    confusedEntry,
    disgustedEntry,
    fearEntry,
    happyEntry,
    sadEntry,
    surprisedEntry,
  ) => {
    // Use the first non-undefined entry for time, or fallback to angryEntry
    const timeEntry = angryEntry || calmEntry || confusedEntry || disgustedEntry
      || fearEntry || happyEntry || sadEntry || surprisedEntry;

    if (!timeEntry) {
      return null;
    }

    return {
      angry: angryEntry?.y || 0,
      calm: calmEntry?.y || 0,
      confused: confusedEntry?.y || 0,
      disgusted: disgustedEntry?.y || 0,
      fear: fearEntry?.y || 0,
      happy: happyEntry?.y || 0,
      sad: sadEntry?.y || 0,
      surprised: surprisedEntry?.y || 0,
      time: formatDateToTimezone(timeEntry.x, timezone, 'hh:mm a'),
    };
  }).filter(Boolean); // Remove any null entries

  const intervalOptions = [
    { value: '1m', label: '1 min' },
    { value: '5m', label: '5 mins' },
    { value: '15m', label: '15 mins' },
    { value: '30m', label: '30 mins' },
  ];

  const intervalDropdown = (
    <Authorize permissions={CONFIG}>
      <Dropdown onSelect={setSelectedInterval}>
        <Dropdown.Toggle
          variant="link"
          size="sm"
          className="p-0 text-muted"
          style={{
            fontSize: '12px',
            textDecoration: 'none',
            border: 'none',
            boxShadow: 'none',
            fontWeight: 'normal',
          }}
        />
        <Dropdown.Menu style={{ minWidth: 'auto', padding: '4px 0' }}>
          {intervalOptions.map((option) => (
            <Dropdown.Item
              key={option.value}
              eventKey={option.value}
              active={selectedInterval === option.value}
              style={{ padding: '4px 12px', fontSize: '12px' }}
            >
              {option.label}
            </Dropdown.Item>
          ))}
        </Dropdown.Menu>
      </Dropdown>
    </Authorize>
  );

  if (!isEmpty(chartData)) {
    return (
      <ChartCard
        title={(
          <Info
            label="Emotions Timeline"
            content="A visualization of the emotions of users in AmplifiCam photos over the event"
            className="ml-2"
          />
        )}
        header={intervalDropdown}
      >
        <Recharts.ResponsiveContainer width="100%" height={350}>
          <Recharts.LineChart data={chartData} isAnimationActive={false}>
            <Recharts.XAxis
              dataKey="time"
              tickLine={false}
              dy={10}
              dx={5}
              tick={{
                fontSize: '12px',
                color: '#B7B8BA',
                marginRight: '10px',
                lineHeight: '27px',
                fontWeigth: '500',
              }}
            />
            <Recharts.YAxis
              tickLine={false}
              dy={-5}
              dx={-5}
              tick={{
                fontSize: '12px',
                color: '#B7B8BA',
                marginRight: '10px',
              }}
            />
            <Recharts.Tooltip content={<ChartTooltip />} />
            <Recharts.Legend
              verticalAlign="top"
              align="right"
              wrapperStyle={{ fontSize: '14px', color: '#4E5155', textTransform: 'capitalize' }}
              iconType="circle"
              iconSize="11"
              onClick={({ dataKey }) => handleLegendClick(dataKey)}
            />
            <Recharts.Line type="monotone" dataKey="angry" stroke="#E74C3C" fill="#E74C3C" dot={false} strokeWidth={2} hide={!chartLines.angry} />
            <Recharts.Line type="monotone" dataKey="calm" stroke="#5DADE2" fill="#5DADE2" dot={false} strokeWidth={2} hide={!chartLines.calm} />
            <Recharts.Line type="monotone" dataKey="confused" stroke="#9A7D0A" fill="#9A7D0A" dot={false} strokeWidth={2} hide={!chartLines.confused} />
            <Recharts.Line type="monotone" dataKey="disgusted" stroke="#117864" fill="#117864" dot={false} strokeWidth={2} hide={!chartLines.disgusted} />
            <Recharts.Line type="monotone" dataKey="fear" stroke="#000000" fill="#000000" dot={false} strokeWidth={2} hide={!chartLines.fear} />
            <Recharts.Line type="monotone" dataKey="happy" stroke="#F7DC6F" fill="#F7DC6F" dot={false} strokeWidth={2} hide={!chartLines.happy} />
            <Recharts.Line type="monotone" dataKey="sad" stroke="#808080" fill="#808080" dot={false} strokeWidth={2} hide={!chartLines.sad} />
            <Recharts.Line type="monotone" dataKey="surprised" stroke="#E67E22" fill="#E67E22" dot={false} strokeWidth={2} hide={!chartLines.surprised} />
          </Recharts.LineChart>
        </Recharts.ResponsiveContainer>
      </ChartCard>
    );
  }

  return null;
};

export default EmotionTimeline;
